/**
 * The list of every asset name the base game already owns, fetched on demand.
 *
 * A FiveM server streams into one flat namespace. There are no folders in it:
 * an add-on that ships `police.yft` is shipping the same name Rockstar ships,
 * and the server loads exactly one of them. That is the single most damaging
 * mistake in this domain, because it does not look like a mistake — the pack
 * installs, the resource starts, and then every stock police car in the city is
 * the author's model, or the author's model is silently replaced by the stock
 * one, depending on load order. To say anything useful about it a tool has to
 * know all 238,359 stock names.
 *
 * The naive shape for that is a generated TypeScript array, the way
 * `data/vanillaVehicles.v1.ts` holds 935 spawn names. At this size that is the
 * wrong answer twice over. Uncompressed the list is 5.7 MB of text, and as a
 * module it would be parsed by the JavaScript engine on every page load of every
 * surface that imports anything from this layer, whether or not the session ever
 * runs a collision check — and most sessions never do. Someone opening Easy Meta
 * to write a `carcols.meta` should not pay 5.7 MB to do it.
 *
 * So the list lives in `public/` as an 857 KB gzip, nothing here touches it at
 * import time, and the first caller that actually needs it pays for the download
 * once. Concurrent callers share that one in-flight promise, and a failed load
 * clears the cache so the next attempt can retry rather than replaying the same
 * rejection forever — the pattern `src/lib/paintbooth/unwrapModel.ts` uses for
 * its WASM.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SCOPE — what this index can and cannot tell you
 *
 * It is a table of contents, not the assets: it says a name exists in the base
 * game, and nothing about what the asset is. It was read from one install at one
 * game build (recorded in `gameBuild`), so a name added by a newer DLC is a name
 * this index does not have, and the honest report for a miss is "we did not find
 * a collision", never "there is no collision". A caller that has its own dump —
 * a different build, or a server's own stream inventory — builds an index from
 * it with `createVanillaAssetNameIndex`, which does no IO at all.
 *
 * Every failure mode of the download is therefore loud on purpose. A half-read
 * list is worse than no list: the names that did not arrive answer "the base game
 * does not own this", the pack ships the override anyway, and nobody sees the
 * problem until the server is live. So a short read is refused with prose rather
 * than indexed, and the manifest's `total` is what makes refusing it possible.
 *
 * Decompression is `pako` behind a dynamic `import()`, so the library stays out
 * of the bundle of every surface that never runs this check. `DecompressionStream`
 * would avoid the dependency entirely, but it needs `Blob.stream()`, which jsdom
 * does not implement, and an unloadable index that cannot be tested is worse than
 * a dependency.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { baseName, extensionOf, normalizeName, stemOf } from "./normalize";
import type { VanillaAssetNameIndex } from "./types";

// ─── Where the data lives ───────────────────────────────────────────────────

/** The gzipped, LF-separated, lower-cased, sorted `name.extension` list. */
export const VANILLA_NAMES_URL = "/data/gta/vanilla-names-v1.txt.gz";

/** Its manifest: version, source, game build, total, per-extension counts. */
export const VANILLA_NAMES_MANIFEST_URL = "/data/gta/vanilla-names-v1.json";

// ─── Limits ─────────────────────────────────────────────────────────────────

/**
 * Cap on the lines read out of a dump.
 *
 * The shipped list is 238,359 names and a future game build only grows it, so
 * this is set far above any real input. It exists because a caller may hand
 * `createVanillaAssetNameIndex` a file a user chose, and a hundred-megabyte
 * paste must not lock up the PackBench report while it is indexed. The text is
 * walked a line at a time rather than split into an array first, so the cap is
 * reached before the memory is spent instead of after.
 */
const MAX_INDEXED_NAMES = 2_000_000;

// ─── Building an index ──────────────────────────────────────────────────────

/**
 * Extensions are stored per stem as a delimited string rather than a `Set`.
 *
 * `hasBasename("police")` has to be a lookup, not a walk over a quarter of a
 * million names, so the stems need their own map — but 238k `Set` objects to
 * hold an average of one extension each is a lot of allocation for a check that
 * is answered just as fast by looking for `|yft|` inside `|yft|ytd|`.
 */
const EXTENSION_DELIMITER = "|";

/**
 * One extension as it is written into a stem's extension string.
 *
 * The delimiter is stripped out of the extension itself, because a user-supplied
 * dump is not a filesystem and can contain anything: a line reading `police.y|ft`
 * would otherwise store `|y|ft|` and answer yes to `hasBasename("police", "ft")`,
 * reporting a collision against an asset nobody ships. Real GTA extensions never
 * contain the character, so this costs the shipped list nothing.
 */
function extensionToken(extension: string): string {
  const safe = extension.split(EXTENSION_DELIMITER).join("");
  return `${EXTENSION_DELIMITER}${safe}${EXTENSION_DELIMITER}`;
}

/**
 * Build a lookup over a plain-text dump of stock asset names.
 *
 * One `name.extension` per line; blank lines, stray whitespace and CRLF endings
 * are ignored, so a hand-made list pasted from a text editor works. This does no
 * IO, which is what lets a caller index its own dump from a different game build
 * and what lets the tests run without a network.
 *
 * `meta` supplies the provenance fields a dump cannot carry — `version`,
 * `source`, `gameBuild`, `generated`. Its `size`, `has` and `hasBasename` are
 * ignored, since those are measured from the text rather than claimed about it.
 *
 * `generated` is the day the list was extracted, and it is carried rather than
 * dropped because the build alone does not date the index: a rebuild against the
 * same build changes which names are in it and nothing a reader can see. A
 * caller indexing its own dump usually has no date, which is why the fallback is
 * `null` and every report prints an em dash rather than guessing one.
 */
export function createVanillaAssetNameIndex(
  text: string,
  meta?: Partial<VanillaAssetNameIndex>,
): VanillaAssetNameIndex {
  const names = new Set<string>();
  const extensionsByStem = new Map<string, string>();

  let read = 0;
  let cursor = 0;
  while (cursor <= text.length && read < MAX_INDEXED_NAMES) {
    let end = text.indexOf("\n", cursor);
    if (end === -1) end = text.length;
    const line = normalizeName(text.slice(cursor, end));
    cursor = end + 1;
    if (line === "") continue;
    read += 1;

    names.add(line);

    // `police+hi.ytd` keeps its `+` here, and has to: the stem is the map key and
    // never reaches a RegExp, so the high-detail texture dictionary is an ordinary
    // name rather than a pattern that would match half the dictionary.
    const stem = stemOf(line);
    const token = extensionToken(extensionOf(line));
    const known = extensionsByStem.get(stem);
    if (known === undefined) {
      extensionsByStem.set(stem, token);
    } else if (!known.includes(token)) {
      extensionsByStem.set(stem, known + token.slice(EXTENSION_DELIMITER.length));
    }
  }

  return {
    version: typeof meta?.version === "number" ? meta.version : 0,
    source: typeof meta?.source === "string" ? meta.source : "supplied text",
    gameBuild: typeof meta?.gameBuild === "string" ? meta.gameBuild : null,
    generated: typeof meta?.generated === "string" ? meta.generated : null,
    size: names.size,

    has(nameWithExtension: string): boolean {
      // A caller holding a stream path rather than a bare name is common, and a
      // path that fails to match would read as "no collision" — the one wrong
      // answer this whole module exists to prevent. Compare the basename.
      return names.has(normalizeName(baseName(nameWithExtension)));
    },

    hasBasename(name: string, extension?: string): boolean {
      const stem = stemOf(normalizeName(baseName(name)));
      const known = extensionsByStem.get(stem);
      if (known === undefined) return false;
      // Omitting the filter asks whether the base game owns the stem at all; an
      // empty string is a real filter, and matches only a name that has no
      // extension, which is what `extensionOf` hands back for one.
      if (extension === undefined) return true;
      return known.includes(extensionToken(normalizeName(extension).replace(/^\./, "")));
    },
  };
}

// ─── Loading the shipped index ──────────────────────────────────────────────

/** The fields the shipped manifest carries that an index wants. */
type VanillaNamesManifest = {
  version?: number;
  source?: string;
  gameBuild?: string;
  /** `2026-09-10` — the day the list was extracted, as the shipped file records it. */
  generated?: string;
  total?: number;
  sha256?: string;
};

/**
 * The resolved index, kept so a synchronous caller can ask whether it is here
 * yet without starting a download it does not want to wait for.
 */
let loadedIndex: VanillaAssetNameIndex | null = null;

/** The in-flight load, so concurrent callers share one 857 KB download. */
let loadPromise: Promise<VanillaAssetNameIndex> | null = null;

/**
 * Fetch, decompress and index the shipped stock name list.
 *
 * The first call downloads; every later call resolves from the module-level
 * cache. A failure clears the cache before it rejects, so a user who lost their
 * connection mid-report can run the check again rather than being told the index
 * is broken for the rest of the session.
 *
 * `fetchImpl` exists for tests and for a desktop host that routes requests
 * through its own transport. It is only consulted on the call that actually
 * performs the load — once an index is cached, everyone gets that one.
 *
 * This is the one function here that throws, and it throws prose meant to be
 * shown: a report can catch it and record the vanilla check as skipped.
 */
export function loadVanillaAssetNameIndex(options?: {
  fetchImpl?: typeof fetch;
}): Promise<VanillaAssetNameIndex> {
  if (!loadPromise) {
    const fetchImpl = options?.fetchImpl ?? globalThis.fetch;
    // Each settled load only touches the cache if it is still the load the cache
    // holds. Without that check, a slow attempt that fails after the caller reset
    // and retried would clear the retry's entry, and the next caller would start a
    // second 857 KB download of a list already on its way.
    const pending: Promise<VanillaAssetNameIndex> = fetchVanillaAssetNameIndex(fetchImpl)
      .then((index) => {
        if (loadPromise === pending) loadedIndex = index;
        return index;
      })
      .catch((error) => {
        if (loadPromise === pending) loadPromise = null;
        throw error;
      });
    loadPromise = pending;
  }
  return loadPromise;
}

/** The index if it has already been loaded, without starting a download. */
export function peekVanillaAssetNameIndex(): VanillaAssetNameIndex | null {
  return loadedIndex;
}

/** Drop the cached index so the next load fetches again. */
export function resetVanillaAssetNameIndex(): void {
  loadedIndex = null;
  loadPromise = null;
}

async function fetchVanillaAssetNameIndex(
  fetchImpl: typeof fetch,
): Promise<VanillaAssetNameIndex> {
  if (typeof fetchImpl !== "function") {
    throw new Error(
      "The stock asset name list can only be downloaded where fetch is available.",
    );
  }

  const [namesResponse, manifestResponse] = await Promise.all([
    fetchImpl(VANILLA_NAMES_URL),
    fetchImpl(VANILLA_NAMES_MANIFEST_URL),
  ]);

  if (!namesResponse.ok) {
    throw new Error(
      "Could not download the stock asset name list " +
        `(vanilla-names-v1.txt.gz, HTTP ${namesResponse.status}).`,
    );
  }
  if (!manifestResponse.ok) {
    throw new Error(
      "Could not download the stock asset name manifest " +
        `(vanilla-names-v1.json, HTTP ${manifestResponse.status}).`,
    );
  }

  const manifest = await readManifest(manifestResponse);
  const compressed = new Uint8Array(await namesResponse.arrayBuffer());

  // Dynamic so pako is only fetched by the sessions that run this check.
  const { ungzip } = await import("pako");
  let text: string;
  try {
    text = new TextDecoder("utf-8").decode(ungzip(compressed));
  } catch {
    // pako's own message here is "incorrect header check", which tells a pack
    // author nothing. The usual cause is a host serving an error page or a proxy
    // that re-encoded the file, and both are fixed by the person deploying it.
    throw new Error(
      "The stock asset name list downloaded but could not be read: " +
        "vanilla-names-v1.txt.gz is not valid gzip. The file was most likely " +
        "replaced by an error page or rewritten in transit.",
    );
  }

  const index = createVanillaAssetNameIndex(text, {
    version: manifest.version,
    // A downloaded index whose manifest was unreadable still came from the shipped
    // file, and a report that cited it as "supplied text" would be lying about
    // where its evidence came from.
    source: typeof manifest.source === "string" ? manifest.source : VANILLA_NAMES_URL,
    gameBuild: manifest.gameBuild,
    generated: manifest.generated,
  });

  // A truncated download is the failure nobody would notice: the names that never
  // arrived answer "the base game does not own this", the pack ships an override
  // of a stock asset, and the first symptom is every police car in the city
  // changing model. Refuse a short list rather than under-report from it.
  if (typeof manifest.total === "number" && index.size < manifest.total) {
    throw new Error(
      `The stock asset name list is incomplete: ${index.size} of ${manifest.total} ` +
        "names arrived, so a collision check would miss the rest. Try again.",
    );
  }

  return index;
}

/**
 * Read the manifest, tolerating a manifest that is not what we expect.
 *
 * The names are the useful part; the manifest only supplies provenance. Failing
 * the whole load because the sidecar JSON is malformed would turn a cosmetic
 * problem — a report that cannot name the game build — into no collision check
 * at all, so a bad manifest degrades to empty provenance instead. The cost is
 * that the completeness check above has nothing to compare against, which is the
 * right trade only because a mangled manifest and a mangled list rarely travel
 * together.
 */
async function readManifest(response: Response): Promise<VanillaNamesManifest> {
  try {
    const parsed: unknown = await response.json();
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as VanillaNamesManifest;
  } catch {
    return {};
  }
}
